import { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { dateLocale } from '../../i18n/i18n';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ReactToPrint from 'react-to-print';
import ConfirmationModal from '../../components/modals/confirmation';
import OverlayLoader from '../../components/overlayLoader';
import Pages from '../../components/pages';
import { deleteExpense, getPaginated } from '../../services/expenses';
import { debounce, rsdFormat, toLocalDateString } from '../../utils/helper';
import { useSheetExport } from '../../utils/queryHooks/sheetExport';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import SetExpenseModal from './expenseSet';
import ExpenseSheetImportModal from './expenseSheetImport';
import ExpensesFilter from './expensesFilter';
import { SkeletonRows } from '../../components/skeleton';

/* ── page-scoped styles (mockup patterns not yet in shared CSS) ── */
const pageStyles = `
  .sbr .exp-row{display:flex;align-items:center;gap:12px;background:var(--card);border:1px solid var(--line);
    border-radius:var(--r-md);padding:12px 13px;margin-bottom:10px;box-shadow:var(--sh-2)}
  .sbr .exp-row .ti{width:40px;height:40px;border-radius:12px;display:flex;align-items:center;justify-content:center;
    flex-shrink:0;border:1px solid var(--line)}
  .sbr .exp-row .ti .ic{width:19px;height:19px}
  .sbr .exp-row .info{flex:1;min-width:0}
  .sbr .exp-row .en{font-size:14px;font-weight:700;color:var(--ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .sbr .exp-row .esub{display:flex;align-items:center;gap:7px;font-size:12px;color:var(--muted);font-weight:600;margin-top:3px}
  .sbr .exp-row .esep{width:3px;height:3px;border-radius:50%;background:#C4CCD8;flex-shrink:0}
  .sbr .exp-row .pay{display:inline-flex;align-items:center;gap:4px}
  .sbr .exp-row .pay .ic{width:13px;height:13px;color:var(--faint)}
  .sbr .exp-row .eamt{margin-left:auto;text-align:right;flex-shrink:0;font-size:14.5px;font-weight:800;
    color:var(--ink);letter-spacing:-.3px;font-variant-numeric:tabular-nums}

  .sbr .cat-ti{width:40px;height:40px;border-radius:12px;display:flex;align-items:center;justify-content:center;
    flex-shrink:0;border:1px solid var(--line)}
  .sbr .cat-ti .ic{width:19px;height:19px}
  .sbr .cat-sal{background:var(--brand-50);border-color:var(--brand-100);color:var(--brand-ink)}
  .sbr .cat-rent{background:var(--cleaning-bg);color:var(--cleaning);border-color:var(--cleaning-bg)}
  .sbr .cat-equip{background:var(--delivery-bg);color:var(--delivery);border-color:var(--delivery-bg)}
  .sbr .cat-det{background:var(--done-bg);color:var(--done);border-color:var(--done-bg)}
  .sbr .cat-pack{background:#EEF2F8;color:#475569;border-color:#DDE6F0}
  .sbr .cat-fuel{background:var(--bad-bg);color:var(--bad);border-color:var(--bad-bg)}
  .sbr .cat-generic{background:var(--surface);color:var(--muted);border-color:var(--line)}

  /* category breakdown in mobile card */
  .sbr .brk{display:flex;flex-direction:column;gap:13px}
  .sbr .brk .brow{display:flex;align-items:center;gap:11px}
  .sbr .brk .bti{width:34px;height:34px;border-radius:11px;display:flex;align-items:center;justify-content:center;
    flex-shrink:0;border:1px solid var(--line)}
  .sbr .brk .bti .ic{width:17px;height:17px}
  .sbr .brk .bmid{flex:1;min-width:0}
  .sbr .brk .blab{display:flex;align-items:baseline;gap:8px;margin-bottom:6px}
  .sbr .brk .bnm{font-size:13px;font-weight:700;color:var(--ink)}
  .sbr .brk .bpc{font-size:11px;font-weight:700;color:var(--faint)}
  .sbr .brk .bamt{margin-left:auto;font-size:12.5px;font-weight:800;color:var(--text);
    font-variant-numeric:tabular-nums}

  /* desktop: ranked category rail */
  .sbr .rank{display:flex;flex-direction:column;gap:14px;margin-top:4px}
  .sbr .rk{display:grid;grid-template-columns:1fr auto;gap:6px 12px;align-items:center}
  .sbr .rk .rlab{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:700;color:var(--ink)}
  .sbr .rk .rlab .rpc{font-size:11px;font-weight:700;color:var(--faint);background:var(--surface);
    border-radius:999px;padding:1px 7px;margin-left:2px}
  .sbr .rk .rval{font-size:12.5px;font-weight:800;color:var(--ink);font-variant-numeric:tabular-nums;text-align:right}
  .sbr .rk .track{grid-column:1 / -1;height:7px}
  .sbr .rk .track>i{background:var(--rkc,var(--brand-grad))}

  .sbr .cat-dot{width:9px;height:9px;border-radius:50%;display:inline-block;flex-shrink:0;
    box-shadow:0 0 0 3px color-mix(in srgb,var(--cd,#94A3B8) 16%,transparent)}
  .sbr .cat-cell{display:flex;align-items:center;gap:9px;font-weight:600;color:var(--text)}
  .sbr .emeth{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:700;letter-spacing:.2px;
    color:var(--muted);background:var(--surface);border:1px solid var(--line);border-radius:var(--r-pill);padding:3px 10px}
  .sbr .emeth .ic{width:12px;height:12px;color:var(--faint)}
  .sbr .evend{font-weight:700;color:var(--ink)}

  /* desktop table */
  .sbr .exp-tbl{width:100%;border-collapse:collapse;font-size:13px}
  .sbr .exp-tbl thead th{font-size:11.5px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;
    color:var(--muted);padding:8px 10px;border-bottom:2px solid var(--line);white-space:nowrap;text-align:left}
  .sbr .exp-tbl thead th.r{text-align:right}
  .sbr .exp-tbl tbody td{padding:10px 10px;border-bottom:1px solid var(--line-soft);color:var(--text);vertical-align:middle}
  .sbr .exp-tbl tbody tr:last-child td{border-bottom:0}
  .sbr .exp-tbl tbody td.eamt-td{color:var(--ink);font-weight:800;font-variant-numeric:tabular-nums;
    letter-spacing:-.2px;text-align:right;white-space:nowrap}

  .sbr .tbl-micro{display:flex;align-items:center;justify-content:space-between;margin-top:16px;padding-top:14px;
    border-top:1px solid var(--line);font-size:12.5px;font-weight:600;color:var(--muted)}
  .sbr .tbl-micro b{color:var(--ink);font-weight:800;font-variant-numeric:tabular-nums}

  .sbr .tblwrap{margin:0;padding:18px 18px 8px}
  .sbr .tblwrap .ch{margin-bottom:6px}

  .sbr .kpi .kpin{position:absolute;top:13px;right:13px;width:30px;height:30px;border-radius:9px;
    background:var(--surface);border:1px solid var(--line);display:flex;align-items:center;
    justify-content:center;color:var(--brand)}
  .sbr .kpi .kpin .ic{width:15px;height:15px}
  .sbr .kpi .ksub{font-size:11.5px;color:var(--muted);font-weight:600;margin-top:2px}

  .sbr .exp-actions{display:flex;gap:6px}
  .sbr .exp-act-btn{background:none;border:none;padding:3px 5px;cursor:pointer;border-radius:6px;
    display:inline-flex;align-items:center;transition:background .15s}
  .sbr .exp-act-btn:hover{background:var(--surface)}
  .sbr .exp-act-btn:disabled{opacity:.4;cursor:not-allowed}
`;

/* ── Icon helpers ── */
const PlusIcon = () => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);
const DownloadIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
);
const ChevronIcon = () => (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9"/>
    </svg>
);
const EditIcon = () => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14H6L5 6"/>
        <path d="M10 11v6M14 11v6"/>
        <path d="M9 6V4h6v2"/>
    </svg>
);
const CardIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
    </svg>
);
const CashIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M2 8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z"/>
        <circle cx="12" cy="12" r="2.5"/>
    </svg>
);
const BankIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <line x1="12" y1="2" x2="12" y2="22"/>
        <path d="M5 7l7-4 7 4M5 7v10l7 4 7-4V7"/>
    </svg>
);
const WrenchIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2-2 2.5-2.5z"/>
    </svg>
);

/* derive a category class from the category string */
const getCatClass = (category = '') => {
    const c = category.toLowerCase();
    if (c.includes('salary') || c.includes('salari') || c.includes('payroll') || c.includes('staff')) return 'cat-sal';
    if (c.includes('rent')) return 'cat-rent';
    if (c.includes('equip')) return 'cat-equip';
    if (c.includes('detergent') || c.includes('supplies') || c.includes('chemical') || c.includes('supply')) return 'cat-det';
    if (c.includes('pack')) return 'cat-pack';
    if (c.includes('fuel') || c.includes('petrol') || c.includes('diesel')) return 'cat-fuel';
    return 'cat-generic';
};

const getPaymentIcon = (method = '') => {
    const m = method.toLowerCase();
    if (m.includes('cash')) return <CashIcon />;
    if (m.includes('bank') || m.includes('transfer')) return <BankIcon />;
    return <CardIcon />;
};

/* group expenses by date label */
const groupByDate = (expenses, t) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups = [];
    const seen = new Map();

    expenses.forEach((expense) => {
        const d = new Date(expense.date);
        d.setHours(0, 0, 0, 0);
        const key = d.getTime();

        let label;
        if (d.getTime() === today.getTime()) label = t('common.today');
        else if (d.getTime() === yesterday.getTime()) label = t('common.yesterday');
        else label = d.toLocaleDateString(dateLocale(), { weekday: 'short', day: 'numeric', month: 'long' });

        const meta = d.toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long' });

        if (!seen.has(key)) {
            seen.set(key, { label, meta, items: [] });
            groups.push(seen.get(key));
        }
        seen.get(key).items.push(expense);
    });

    return groups;
};

/* ── Main component ── */
const ExpensesPage = () => {

    const { t } = useTranslation();
    const [expenseModal, setExpenseModal] = useState(false);
    const [expenseSheetImportModal, setExpenseSheetImportModal] = useState(false);
    const [confirmationModal, setConfirmationModal] = useState(false);
    const [expenses, setExpenses] = useState([]);
    const [expenseToDelete, setExpenseToDelete] = useState();
    const [expenseToEdit, setExpenseToEdit] = useState();
    const [pagination, setPagination] = useState();
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [filters, setFilters] = useState(['', '']);
    const [searchText, setSearchText] = useState('');

    const { exportSheet: exportExcel, isExporting } = useSheetExport('Expenses',
        expenses.map(({ id, companyId, createdById, notes, updatedById, ...rest }) => rest),
        ['Date', 'Amount', 'Category', 'Description', 'Payment Method', 'Created At', 'Updated At']);
    const { exportSheet: exportCSV, isExportingCSV } = useSheetExport('Expenses',
        expenses.map(({ id, companyId, createdById, notes, updatedById, ...rest }) => rest),
        ['Date', 'Amount', 'Category', 'Description', 'Payment Method', 'Created At', 'Updated At'], 'csv');

    const printRef = useRef();
    const queryClient = useQueryClient();

    const { data, isFetching } = useQuery(
        ['expenses', 'paginated', pageNumber, pageSize, ...filters, searchText],
        () => getPaginated(pageNumber, pageSize, ...filters, searchText)
    );

    const { mutate, isLoading: isDeleting } = useMutation(deleteExpense, {
        onSuccess: onMutateSuccess(() => {
            if (expenses.length === 1 && pageNumber > 1) setPageNumber(pageNumber - 1);
            queryClient.invalidateQueries(['expenses', 'paginated']);
        })
    });

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setPagination(result);
            setExpenses(result.data.map(x => {
                x.amount = parseFloat(x.amount);
                const { amount, ...rest } = x;
                return { date: x.date, amount, ...rest };
            }));
        });
    }, [data]);

    const handleDeleteExpense         = () => mutate(expenseToDelete.id);
    const handleShowConfirmation      = (expense) => { setConfirmationModal(true); setExpenseToDelete(expense); };
    const handleShowSetExpenseModal   = (expense) => { setExpenseToEdit(expense); setExpenseModal(true); };
    const handleSearchText            = debounce((e) => setSearchText(e.target.value));
    const handleSetPageSize           = (size) => { setPageNumber(1); setPageSize(size); };

    /* totals for KPI */
    const totalAmount = expenses.reduce((s, e) => s + (e.amount || 0), 0);
    const expenseCount = pagination?.totalCount ?? expenses.length;

    /* category totals */
    const catTotals = expenses.reduce((acc, e) => {
        const cat = e.category || 'Other';
        acc[cat] = (acc[cat] || 0) + (e.amount || 0);
        return acc;
    }, {});
    const sortedCats = Object.entries(catTotals).sort((a, b) => b[1] - a[1]);
    const topCat = sortedCats[0];
    const topCatPct = topCat && totalAmount ? ((topCat[1] / totalAmount) * 100).toFixed(1) : '0';

    const dateGroups = groupByDate(expenses, t);

    return (
        <div className="sbr">
            <style>{pageStyles}</style>

            <ConfirmationModal display={confirmationModal} toggle={setConfirmationModal} title={t('expenses.deleteExpense')}
                message={t('expenses.deleteExpenseConfirm')} confirmAction={handleDeleteExpense} />
            <SetExpenseModal display={expenseModal} toggle={setExpenseModal} expenseId={expenseToEdit?.id} onClose={() => setExpenseToEdit()} />
            <ExpenseSheetImportModal display={expenseSheetImportModal} toggle={setExpenseSheetImportModal} />

            {/* ── Desktop header ── */}
            <div className="dhead sbr-d">
                <div>
                    <div className="ttl">{t('expenses.title')}</div>
                    <div className="sub">
                        {expenseCount > 0
                            ? `${expenseCount} ${t('expenses.entriesLogged')}`
                            : t('expenses.trackAndManage')}
                    </div>
                </div>
                <div className="act">
                    <Dropdown>
                        <Dropdown.Toggle as="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" id="expenses-export" disabled={isFetching}>
                            <DownloadIcon /> {t('expenses.exportLabel')} <ChevronIcon />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item as="button"><ReactToPrint trigger={() => <span>{t('expenses.print')}</span>} content={() => printRef.current} /></Dropdown.Item>
                            <Dropdown.Item as="button" disabled={isExportingCSV} onClick={() => exportCSV()}>{isExportingCSV ? t('expenses.exporting') : t('expenses.csv')}</Dropdown.Item>
                            <Dropdown.Item as="button" disabled={isExporting} onClick={() => exportExcel()}>{isExporting ? t('expenses.exporting') : t('expenses.excel')}</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleShowSetExpenseModal()}>
                        <PlusIcon /> {t('expenses.addExpense')}
                    </button>
                </div>
            </div>

            {/* ── Mobile appbar ── */}
            <div className="sbr-m" style={{ marginBottom:'16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <div style={{ flex:1, fontSize:'18px', fontWeight:800, color:'var(--ink)', letterSpacing:'-.4px' }}>{t('expenses.title')}</div>
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleShowSetExpenseModal()}>
                        <PlusIcon /> {t('common.add')}
                    </button>
                </div>
            </div>

            {/* ── Filters ── */}
            <div style={{ marginBottom: '18px' }}>
                <ExpensesFilter onFilter={setFilters} />
            </div>

            {/* ── KPIs — identical 4 cards on mobile + desktop (4-col → 2-col → 1-col) ── */}
            <div className="kpis4">
                <div className="kpi lift">
                    <div className="l">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                        </svg>
                        {t('expenses.totalThisMonth')}
                    </div>
                    <div className="n">{rsdFormat(totalAmount)} <span style={{ fontSize: '14px', fontWeight: 700, opacity: .7 }}>RSD</span></div>
                    <div className="ksub">{sortedCats.length === 1 ? t('expenses.acrossCategory', { count: sortedCats.length }) : t('expenses.acrossCategories', { count: sortedCats.length })}</div>
                </div>
                <div className="kpi">
                    <div className="kpin">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M3 9.5 12 3l9 6.5V21H3z"/><path d="M9 21v-7h6v7"/>
                        </svg>
                    </div>
                    <div className="l">{t('expenses.largestCategory')}</div>
                    <div className="n">{topCat ? rsdFormat(topCat[1]) : '—'} <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--muted)' }}>RSD</span></div>
                    <div className="ksub">{topCat ? t('expenses.ofSpend', { name: topCat[0], pct: topCatPct }) : '—'}</div>
                </div>
                <div className="kpi">
                    <div className="kpin">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/>
                        </svg>
                    </div>
                    <div className="l">{t('expenses.numberOfExpenses')}</div>
                    <div className="n">{expenseCount}</div>
                    <div className="ksub">{t('expenses.entriesLogged')}</div>
                </div>
                <div className="kpi">
                    <div className="kpin">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                            <polyline points="17 6 23 6 23 12"/>
                        </svg>
                    </div>
                    <div className="l">{t('expenses.pageSlashSize')}</div>
                    <div className="n">{pageNumber} <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--muted)' }}>/ {Math.max(pagination?.totalPages || 1, 1)}</span></div>
                    <div className="ksub">{t('expenses.pageOfTotal', { page: pageNumber, total: Math.max(pagination?.totalPages || 1, 1) })}</div>
                </div>
            </div>

            {/* ── Desktop: ledger + category rail ── */}
            <div className="cols sbr-d">

                {/* Ledger table */}
                <div className="card-sbr tblwrap" style={{ position: 'relative' }}>
                    <div className="ch">
                        <svg className="ci ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
                            <line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>
                            <line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                        </svg>
                        <span className="t">{t('expenses.recentExpenses')}</span>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <div style={{ position: 'relative' }}>
                                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                    style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                </svg>
                                <input style={{ paddingLeft: '30px', fontSize: '12.5px', border: '1px solid var(--line)', borderRadius: '8px', padding: '6px 10px 6px 28px', outline: 'none', background: 'var(--surface)', color: 'var(--ink)', width: '190px' }}
                                    type="search" placeholder={t('expenses.searchExpenses')} onChange={handleSearchText} />
                            </div>
                            <select style={{ fontSize: '12px', border: '1px solid var(--line)', borderRadius: '8px', padding: '6px 10px', background: 'var(--surface)', color: 'var(--text)' }}
                                onChange={(e) => handleSetPageSize(e.target.value)} defaultValue="10">
                                <option value="10">{t('expenses.perPage', { n: 10 })}</option>
                                <option value="20">{t('expenses.perPage', { n: 20 })}</option>
                                <option value="50">{t('expenses.perPage', { n: 50 })}</option>
                                <option value="100">{t('expenses.perPage', { n: 100 })}</option>
                            </select>
                        </div>
                    </div>

                    <div ref={printRef} style={{ position: 'relative', minHeight: '80px' }}>
                        {isFetching && expenses.length === 0 ? (
                            <SkeletonRows rows={6} widths={['10%', '16%', '22%', '16%', '14%', '10%']} />
                        ) : expenses.length === 0 ? (
                            <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--muted)', fontStyle: 'italic', fontSize: '14px' }}>
                                {t('expenses.noExpenses')}
                            </div>
                        ) : (
                            <table className="exp-tbl">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>{t('expenses.date')}</th>
                                        <th>{t('common.category')}</th>
                                        <th>{t('expenses.description')}</th>
                                        <th>{t('expenses.method')}</th>
                                        <th className="r">{t('expenses.amountRsd')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expenses.map((expense) => (
                                        <tr key={expense.id}>
                                            <td>
                                                <div className="exp-actions">
                                                    <button
                                                        title={t('common.edit')}
                                                        className="exp-act-btn"
                                                        disabled={!!expense?.payroll?.id}
                                                        style={{ color: 'var(--brand)' }}
                                                        onClick={() => handleShowSetExpenseModal(expense)}>
                                                        <EditIcon />
                                                    </button>
                                                    <button
                                                        title={t('common.delete')}
                                                        className="exp-act-btn"
                                                        disabled={!!expense?.payroll?.id}
                                                        style={{ color: '#94A3B8' }}
                                                        onClick={() => handleShowConfirmation(expense)}>
                                                        <TrashIcon />
                                                    </button>
                                                </div>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{toLocalDateString(expense.date)}</td>
                                            <td>
                                                <span className="cat-cell">
                                                    <span className="cat-dot" style={{ '--cd': 'var(--brand-700)' }}></span>
                                                    {expense.category || '—'}
                                                </span>
                                            </td>
                                            <td className="evend">{expense.description || expense.notes || '—'}</td>
                                            <td>
                                                <span className="emeth">
                                                    {getPaymentIcon(expense.paymentMethod)}
                                                    {expense.paymentMethod || '—'}
                                                </span>
                                            </td>
                                            <td className="eamt-td">{rsdFormat(expense.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {((isFetching && expenses.length > 0) || isDeleting) && <OverlayLoader />}
                    </div>

                    <div className="tbl-micro">
                        <span>{t('expenses.showingEntries', { shown: expenses.length, total: expenseCount })}</span>
                        <span>{t('expenses.totalOnPage')} · <b>{rsdFormat(totalAmount)} RSD</b></span>
                    </div>

                    {pagination?.totalPages > 0 && (
                        <div style={{ paddingTop: '12px', paddingBottom: '8px' }}>
                            <Pages paginationData={pagination} changePage={setPageNumber} pageNumber={pageNumber} />
                        </div>
                    )}
                </div>

                {/* Ranked category rail */}
                <div className="card-sbr" style={{ margin: 0 }}>
                    <div className="ch">
                        <svg className="ci ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M18 20V10M12 20V4M6 20v-6"/>
                        </svg>
                        <span className="t">{t('expenses.byCategory')}</span>
                    </div>
                    {sortedCats.length === 0 ? (
                        <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>{t('common.noData')}</div>
                    ) : (
                        <div className="rank">
                            {sortedCats.map(([cat, amt], i) => {
                                const pct = totalAmount ? ((amt / totalAmount) * 100).toFixed(1) : '0';
                                const barWidth = totalAmount ? `${(amt / sortedCats[0][1]) * 100}%` : '0%';
                                const gradients = [
                                    'linear-gradient(150deg,#3B74F2 0%,#1D4FD8 100%)',
                                    'linear-gradient(150deg,#F2A53B,#D97706)',
                                    'linear-gradient(150deg,#9F6BF0,#7C3AED)',
                                    'linear-gradient(150deg,#2BC48A,#059669)',
                                    'linear-gradient(150deg,#38BDF8,#0EA5E9)',
                                    'linear-gradient(150deg,#EF4444,#DC2626)',
                                    'linear-gradient(150deg,#94A3B8,#64748B)',
                                    'linear-gradient(150deg,#EC4899,#DB2777)',
                                    'linear-gradient(150deg,#2DD4BF,#14B8A6)',
                                ];
                                return (
                                    <div key={cat} className="rk" style={{ '--rkc': gradients[i % gradients.length] }}>
                                        <div className="rlab">
                                            <span className="cat-dot" style={{ '--cd': 'var(--brand-700)' }}></span>
                                            {cat}
                                            <span className="rpc">{pct}%</span>
                                        </div>
                                        <div className="rval">{rsdFormat(amt)}</div>
                                        <div className="track"><i style={{ width: barWidth }}></i></div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div className="tbl-micro">
                        <span>{sortedCats.length} {sortedCats.length === 1 ? t('expenses.category') : t('expenses.categories')}</span>
                        <span>{t('common.total')} <b>{rsdFormat(totalAmount)} RSD</b></span>
                    </div>
                </div>
            </div>

            {/* ── Mobile: category breakdown card ── */}
            <div className="card-sbr sbr-m" style={{ marginBottom: '14px' }}>
                <div className="ch">
                    <svg className="ci ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/>
                        <path d="M22 12A10 10 0 0 0 12 2v10z"/>
                    </svg>
                    <div className="t">{t('expenses.byCategory')}</div>
                </div>
                {sortedCats.length === 0 ? (
                    <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>{t('common.noData')}</div>
                ) : (
                    <div className="brk">
                        {sortedCats.map(([cat, amt]) => {
                            const pct = totalAmount ? ((amt / totalAmount) * 100).toFixed(1) : '0';
                            const barWidth = totalAmount ? `${(amt / sortedCats[0][1]) * 100}%` : '0%';
                            return (
                                <div key={cat} className="brow">
                                    <div className={`bti ${getCatClass(cat)}`}>
                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                                        </svg>
                                    </div>
                                    <div className="bmid">
                                        <div className="blab">
                                            <span className="bnm">{cat}</span>
                                            <span className="bpc num">{pct}%</span>
                                            <span className="bamt num">{rsdFormat(amt)}</span>
                                        </div>
                                        <div className="track">
                                            <i style={{ width: barWidth }}></i>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ── Mobile: expenses grouped by date ── */}
            <div className="sbr-m">
                {isFetching && expenses.length === 0 ? (
                    <SkeletonRows rows={4} widths={['10%', '40%', '20%', '15%']} />
                ) : expenses.length === 0 ? (
                    <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--muted)', fontStyle: 'italic', fontSize: '14px' }}>
                        {t('expenses.noExpenses')}
                    </div>
                ) : (
                    <>
                        {dateGroups.map(({ label, meta, items }) => (
                            <div key={label}>
                                <div className="daygroup">
                                    <span className="d">{label}</span>
                                    <span className="dot"></span>
                                    <span className="meta">{meta}</span>
                                    <span className="count num">{items.length} {items.length === 1 ? t('expenses.item') : t('expenses.items')}</span>
                                </div>
                                {items.map((expense) => (
                                    <div key={expense.id} className="exp-row" onClick={() => handleShowSetExpenseModal(expense)} style={{ cursor: 'pointer' }}>
                                        <div className={`ti cat-ti ${getCatClass(expense.category)}`}>
                                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <rect x="1" y="4" width="22" height="16" rx="2"/>
                                                <line x1="1" y1="10" x2="23" y2="10"/>
                                            </svg>
                                        </div>
                                        <div className="info">
                                            <div className="en">{expense.description || expense.notes || '—'}</div>
                                            <div className="esub">
                                                {expense.category || '—'}
                                                {expense.paymentMethod && (
                                                    <>
                                                        <span className="esep"></span>
                                                        <span className="pay">
                                                            {getPaymentIcon(expense.paymentMethod)}
                                                            {expense.paymentMethod}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="eamt num">{rsdFormat(expense.amount)}</div>
                                    </div>
                                ))}
                            </div>
                        ))}

                        {pagination?.totalPages > 0 && (
                            <div style={{ paddingTop: '8px' }}>
                                <Pages paginationData={pagination} changePage={setPageNumber} pageNumber={pageNumber} />
                            </div>
                        )}
                    </>
                )}
                {((isFetching && expenses.length > 0) || isDeleting) && <OverlayLoader />}
            </div>

            <div className="spacer"></div>
        </div>
    );
};

export default ExpensesPage;
